import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { companyService } from "../services";
import { authStore } from "@/features/auth";
import { validateCompanyFormStep } from "../schemas";

const STORAGE_KEY = "nxstart.onboard.v1";
const MAX_STEP = 5;

const createForm = () => ({
  companyName: "",
  country: "",
  industry: "",
  employeesRange: "",
  revenueRange: "",
  serviceCountries: [],
  objectives: [],
  targetFrequency: "yearly",
  dashboardVariant: "standard",
  enableMonthlyYearlySwitch: false,
  enableViewDetailsSwitch: false,
  useJJOrganizationSelector: false,
  showOnlyMetricsWithTargetAndData: false,
});

const clampStep = (value) => {
  const step = Number(value) || 1;
  return Math.min(Math.max(step, 1), MAX_STEP);
};

const resolvePayload = (payload) => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return payload;
};

const getCompanyId = (company) =>
  company?.id ?? company?._id ?? company?.companyId ?? null;

const buildCompanyFormPatch = (company) => {
  if (!company || typeof company !== "object") return null;
  const patch = {};
  const name = company.companyName ?? company.name;
  if (name) patch.companyName = name;
  if (company.country) patch.country = company.country;
  if (company.industry) patch.industry = company.industry;
  if (company.employeesRange) patch.employeesRange = company.employeesRange;
  if (company.revenueRange) patch.revenueRange = company.revenueRange;
  if (Array.isArray(company.serviceCountries))
    patch.serviceCountries = company.serviceCountries;
  if (Array.isArray(company.objectives)) patch.objectives = company.objectives;
  if (company.targetFrequency) patch.targetFrequency = company.targetFrequency;
  if (company.dashboardVariant) {
    patch.dashboardVariant =
      company.dashboardVariant === "benchmark" ? "benchmark" : "standard";
  }
  if (typeof company.enableMonthlyYearlySwitch === "boolean") {
    patch.enableMonthlyYearlySwitch = company.enableMonthlyYearlySwitch;
  }
  if (typeof company.enableViewDetailsSwitch === "boolean") {
    patch.enableViewDetailsSwitch = company.enableViewDetailsSwitch;
  }
  if (typeof company.useJJOrganizationSelector === "boolean") {
    patch.useJJOrganizationSelector = company.useJJOrganizationSelector;
  }
  if (typeof company.showOnlyMetricsWithTargetAndData === "boolean") {
    patch.showOnlyMetricsWithTargetAndData =
      company.showOnlyMetricsWithTargetAndData;
  }
  return Object.keys(patch).length > 0 ? patch : null;
};

const normalizeText = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeSelectableValue = (item) => {
  if (typeof item === "string") return normalizeText(item);
  if (typeof item === "number") return normalizeText(String(item));
  if (item && typeof item === "object") {
    const resolved = item.value ?? item.key ?? item.id;
    if (typeof resolved === "string" || typeof resolved === "number") {
      return normalizeText(String(resolved));
    }
  }
  return "";
};

const normalizeArrayValues = (value) =>
  Array.isArray(value)
    ? value.map(normalizeSelectableValue).filter(Boolean)
    : [];

const buildCompanyRequestPayload = (form = {}) => {
  const payload = {
    companyName: normalizeText(form?.companyName),
    country: normalizeText(form?.country),
    industry: normalizeText(form?.industry),
    employeesRange: normalizeText(form?.employeesRange),
  };

  const revenueRange = normalizeText(form?.revenueRange);
  const serviceCountries = normalizeArrayValues(form?.serviceCountries);
  const objectives = normalizeArrayValues(form?.objectives);
  const targetFrequency =
    form?.targetFrequency === "monthly" ? "monthly" : "yearly";

  if (revenueRange) payload.revenueRange = revenueRange;
  payload.serviceCountries = serviceCountries;
  payload.objectives = objectives;
  payload.targetFrequency = targetFrequency;
  if (authStore.user?.role === "system_admin") {
    payload.dashboardVariant =
      form?.dashboardVariant === "benchmark" ? "benchmark" : "standard";
  }
  payload.enableMonthlyYearlySwitch = form?.enableMonthlyYearlySwitch ?? false;
  payload.enableViewDetailsSwitch = form?.enableViewDetailsSwitch ?? false;
  payload.useJJOrganizationSelector = form?.useJJOrganizationSelector ?? false;
  payload.showOnlyMetricsWithTargetAndData =
    form?.showOnlyMetricsWithTargetAndData ?? false;

  return payload;
};

const resolveCompanySaveErrorMessage = (error) => {
  const message = normalizeText(error?.message || "");
  if (!message) return "Unable to save company details.";

  if (
    message.includes("is not allowed to be empty") ||
    message.includes("must contain at least 1 items")
  ) {
    return "Please complete required company information before saving.";
  }

  return message;
};

class CompanyStore {
  storage = typeof window !== "undefined" ? window.sessionStorage : null;
  step = 1;
  loading = false;
  companyLoading = false;
  company = null;
  companies = [];
  companiesLoading = false;
  companiesError = "";
  companiesLoaded = false;
  adminCompanies = [];
  adminCompaniesLoading = false;
  adminCompaniesError = "";
  adminCompaniesLoaded = false;
  adminCompaniesMeta = { page: 1, limit: 10, total: 0, totalPages: 1 };
  adminCompaniesQuery = { page: 1, limit: 10, search: "" };
  form = createForm();
  _fetchPromise = null;
  _companiesPromise = null;
  _adminCompaniesPromise = null;

  constructor() {
    makeAutoObservable(this);
    this.loadDraft();
  }

  loadDraft = () => {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved || typeof saved !== "object") return;
      if (typeof saved.step === "number") {
        this.step = Math.min(Math.max(saved.step, 1), 4);
      }
      if (saved.form && typeof saved.form === "object") {
        this.form = { ...this.form, ...saved.form };
      }
    } catch {
      // Ignore draft errors.
    }
  };

  getDraftOwnerId = () => {
    const user = authStore?.user;
    return user?.userId ?? user?.id ?? user?._id ?? user?.email ?? null;
  };

  setStep = (next) => {
    this.step = clampStep(next);
    this.saveDraft();
  };

  next = () => {
    this.setStep(Math.min(this.step + 1, MAX_STEP));
  };

  prev = () => {
    this.setStep(Math.max(this.step - 1, 1));
  };

  setField = (key, value) => {
    this.form = { ...this.form, [key]: value };
    this.saveDraft();
  };

  setForm = (patch) => {
    this.form = { ...this.form, ...patch };
    this.saveDraft();
  };

  hydrateForm = (patch) => {
    if (!patch || typeof patch !== "object") return;
    this.form = { ...this.form, ...patch };
  };

  setCompany = (nextCompany) => {
    this.company = nextCompany ?? null;
  };

  reset = (options = {}) => {
    const persist = options?.persist !== false;
    this.step = 1;
    this.form = createForm();
    if (persist) {
      this.saveDraft();
    }
  };

  useSampleData = () => {
    this.setForm({
      companyName: "Acme Inc.",
      country: "singapore",
      industry: "food_083",
      employeesRange: "51 - 200",
      revenueRange: "2M - 10M",
      serviceCountries: ["singapore", "taiwan"],
      objectives: ["electricity", "fuel"],
      targetFrequency: "yearly",
      dashboardVariant: "standard",
      enableMonthlyYearlySwitch: false,
      enableViewDetailsSwitch: false,
      useJJOrganizationSelector: false,
      showOnlyMetricsWithTargetAndData: false,
    });
  };

  validateStep = (step = this.step, form = this.form) =>
    validateCompanyFormStep(step, form);

  get fieldKeys() {
    return [
      "companyName",
      "country",
      "industry",
      "employeesRange",
      "revenueRange",
      "serviceCountries",
      "objectives",
    ];
  }

  get filledCount() {
    return this.fieldKeys.reduce((acc, key) => {
      const value = this.form[key];
      if (Array.isArray(value)) return acc + (value.length > 0 ? 1 : 0);
      if (typeof value === "boolean") return acc + (value ? 1 : 0);
      if (typeof value === "string")
        return acc + (value.trim().length > 0 ? 1 : 0);
      return acc;
    }, 0);
  }

  get totalCount() {
    return this.fieldKeys.length;
  }

  get progress() {
    return Math.round((this.filledCount / this.totalCount) * 100);
  }

  get progressGradient() {
    const progress = this.progress;
    const hue = Math.max(0, Math.min(120, Math.round((progress / 100) * 120)));
    const start = Math.max(0, hue - 25);
    const end = Math.min(140, hue + 10);
    return `linear-gradient(90deg, hsl(${start}, 92%, 55%), hsl(${end}, 85%, 45%))`;
  }

  get companyOptions() {
    if (!Array.isArray(this.companies)) return [];
    return this.companies
      .map((company) => ({
        value: getCompanyId(company) || "",
        label: company?.companyName || company?.name || "Company",
      }))
      .filter((option) => option.value);
  }

  get canContinue() {
    return Object.keys(this.validateStep()).length === 0;
  }

  upsertCompany = (company) => {
    const companyId = getCompanyId(company);
    if (!company || !companyId) return null;

    const normalizedCompanyId = String(companyId);
    const existingIndex = this.companies.findIndex(
      (item) => String(getCompanyId(item)) === normalizedCompanyId,
    );

    if (existingIndex >= 0) {
      this.companies = this.companies.map((item, index) =>
        index === existingIndex ? { ...item, ...company } : item,
      );
    } else {
      this.companies = [company, ...this.companies];
    }

    if (this.adminCompaniesLoaded) {
      const adminIndex = this.adminCompanies.findIndex(
        (item) => String(getCompanyId(item)) === normalizedCompanyId,
      );
      if (adminIndex >= 0) {
        this.adminCompanies = this.adminCompanies.map((item, index) =>
          index === adminIndex ? { ...item, ...company } : item,
        );
      } else {
        this.adminCompanies = [company, ...this.adminCompanies];
      }
    }

    this.companiesLoaded = true;
    return company;
  };

  removeCompanyFromCache = (companyId) => {
    if (!companyId) return;

    const normalizedCompanyId = String(companyId);
    const isMatchingCompany = (company) =>
      String(getCompanyId(company)) === normalizedCompanyId;

    this.companies = this.companies.filter(
      (company) => !isMatchingCompany(company),
    );

    if (this.adminCompaniesLoaded) {
      this.adminCompanies = this.adminCompanies.filter(
        (company) => !isMatchingCompany(company),
      );
    }
  };

  saveDraftSnapshot = ({ step = this.step, form = this.form } = {}) => {
    try {
      const ownerId = this.getDraftOwnerId();
      const draft = {
        step: Math.min(clampStep(step), 4),
        form,
        ...(ownerId ? { ownerId } : {}),
      };
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch {
      // Ignore draft errors.
    }
  };

  saveDraft = () => {
    this.saveDraftSnapshot({ step: this.step, form: this.form });
  };

  syncDraftOwner = (ownerId = this.getDraftOwnerId()) => {
    if (!this.storage || !ownerId) return;
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      const storedOwner = saved?.ownerId ?? null;
      if (!storedOwner || storedOwner !== ownerId) {
        this.clearDraft();
        this.reset({ persist: false });
      }
    } catch {
      this.clearDraft();
      this.reset({ persist: false });
    }
  };

  clearDraft = () => {
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  };

  fetchCompany = async (input) => {
    const options =
      typeof input === "object" && input !== null && !Array.isArray(input)
        ? input
        : { companyId: input };

    const force = Boolean(options?.force);
    const overrideCompanyId =
      options?.companyId ?? options?.companyID ?? options?.id ?? null;

    if (!force && this._fetchPromise) {
      return this._fetchPromise;
    }

    const companyId =
      overrideCompanyId ??
      authStore?.user?.companyId ??
      getCompanyId(this.company);

    if (!companyId) {
      runInAction(() => {
        this.company = null;
        this.companyLoading = false;
      });
      return Promise.resolve(null);
    }

    runInAction(() => {
      this.companyLoading = true;
    });

    const request = companyService
      .getCompany(companyId, { throwOnError: false })
      .then((data) => {
        if (!data) {
          throw new Error("Unable to load company details.");
        }
        const payload = resolvePayload(data);
        const company = payload?.company ?? payload;
        const patch = buildCompanyFormPatch(company);
        runInAction(() => {
          if (patch) this.hydrateForm(patch);
          this.company = company || null;
        });
        return company;
      })
      .catch((error) => {
        runInAction(() => {
          this.company = null;
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.companyLoading = false;
        });
        this._fetchPromise = null;
      });

    this._fetchPromise = request;
    return request;
  };

  createCompany = async ({
    formValues = this.form,
    companyId,
    options = {},
  }) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    const payload = buildCompanyRequestPayload(formValues);
    this.loading = true;

    try {
      const actionPromise = companyId
        ? companyService.updateCompany(companyId, payload)
        : companyService.createCompany(payload);

      if (shouldToast) {
        showToast.promise(actionPromise, {
          loading: "Saving company...",
          success: "Company saved successfully.",
          error: (err) => err.message,
          ...toastMessages,
        });
      }

      const data = await actionPromise;
      const resolvedPayload = resolvePayload(data);
      const company = resolvedPayload?.company ?? resolvedPayload;
      runInAction(() => {
        if (company && typeof company === "object") {
          this.company = company;
          this.upsertCompany(company);
        }
      });

      const savedCompanyId = getCompanyId(company) ?? companyId;
      const currentCompanyId = authStore?.user?.companyId;
      const shouldRefreshCurrentUser =
        !companyId ||
        (savedCompanyId &&
          currentCompanyId &&
          String(savedCompanyId) === String(currentCompanyId));

      if (shouldRefreshCurrentUser) {
        await authStore.refreshMe();
      }

      this.clearDraft();
      return company;
    } catch (error) {
      const wrapped = new Error(resolveCompanySaveErrorMessage(error));
      wrapped.cause = error;
      throw wrapped;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  fetchPublicCompanies = async (options = {}) => {
    const force = Boolean(options?.force);
    if (!force) {
      if (this._companiesPromise) return this._companiesPromise;
      if (this.companiesLoaded) return Promise.resolve(this.companies);
    }

    runInAction(() => {
      this.companiesLoading = true;
      this.companiesError = "";
      if (force) {
        this.companiesLoaded = false;
      }
    });

    const request = companyService
      .listPublicCompanies({ throwOnError: false })
      .then((data) => {
        if (!data) {
          throw new Error("Unable to load companies.");
        }
        const payload = resolvePayload(data) || {};
        const companies =
          payload?.companies ||
          payload?.data?.companies ||
          payload?.data ||
          payload;
        runInAction(() => {
          this.companies = Array.isArray(companies) ? companies : [];
          this.companiesLoaded = true;
        });
        return this.companies;
      })
      .catch((error) => {
        const message = error?.message || "Unable to load companies.";
        runInAction(() => {
          this.companies = [];
          this.companiesError = message;
          this.companiesLoaded = true;
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.companiesLoading = false;
        });
        this._companiesPromise = null;
      });

    this._companiesPromise = request;
    return request;
  };

  fetchCompanies = async (options = {}) => {
    const force = Boolean(options?.force);
    if (!force) {
      if (this._adminCompaniesPromise) return this._adminCompaniesPromise;
      if (this.adminCompaniesLoaded)
        return Promise.resolve(this.adminCompanies);
    }

    runInAction(() => {
      this.adminCompaniesLoading = true;
      this.adminCompaniesError = "";
      if (force) {
        this.adminCompaniesLoaded = false;
      }
    });

    const page = Number(options?.page) || 1;
    const limit = Number(options?.limit) || 10;
    const search =
      typeof options?.search === "string" ? options.search.trim() : "";

    const currentQuery = { page, limit, search };
    if (
      !force &&
      this.adminCompaniesLoaded &&
      this.adminCompaniesQuery.page === page &&
      this.adminCompaniesQuery.limit === limit &&
      this.adminCompaniesQuery.search === search
    ) {
      return Promise.resolve(this.adminCompanies);
    }

    this.adminCompaniesQuery = currentQuery;
    const request = companyService
      .listCompanies({ page, limit, search }, { throwOnError: false })
      .then((data) => {
        if (!data) {
          throw new Error("Unable to load companies.");
        }
        const payload = resolvePayload(data) || {};
        const companies = Array.isArray(payload?.companies)
          ? payload.companies
          : payload?.data?.companies || payload?.data || payload;
        const pagination = payload?.pagination || payload?.meta || {};
        const total =
          Number(pagination?.total) ||
          (Array.isArray(companies) ? companies.length : 0);
        const pages =
          Number(pagination?.totalPages) ||
          Math.max(1, Math.ceil(total / limit));

        runInAction(() => {
          this.adminCompanies = Array.isArray(companies) ? companies : [];
          this.adminCompaniesMeta = {
            page,
            limit,
            total,
            totalPages: pages,
          };
          this.adminCompaniesLoaded = true;
        });
        return this.adminCompanies;
      })
      .catch((error) => {
        const message = error?.message || "Unable to load companies.";
        runInAction(() => {
          this.adminCompanies = [];
          this.adminCompaniesError = message;
          this.adminCompaniesLoaded = true;
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.adminCompaniesLoading = false;
        });
        this._adminCompaniesPromise = null;
      });

    this._adminCompaniesPromise = request;
    return request;
  };
}

export const companyStore = new CompanyStore();
